import React, { useState, useContext, useRef } from 'react';
import AvatarEditor from 'react-avatar-editor';
import { withRouter } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext.js';
import { useTranslation } from 'react-i18next';
import { Grid, Typography, Avatar, Slider,
	 Button, Input, CircularProgress, Dialog, AppBar, Toolbar } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import HeaderBar from './HeaderBar.js';
import { Transition } from '../Common.js';
import firebase from '../Firebase.js';

const useStyles = makeStyles(
  (theme) =>
    createStyles({
      avatar: {
	width: 120,
	height: 120,
      },
      container: {
	paddingTop: theme.spacing(3),
      },
      saveBtn: {
	width: 100,
	color: theme.palette.primary.main,
	borderRadius: 25,
	background: theme.palette.primary.green,
	'&:hover': {
	  background: theme.palette.primary.green,
	},
      },
      avatarEditorContainer: {
	width: '100%',
	[theme.breakpoints.up('sm')] : {
	  height: `calc(100% - 64px)`
	},
	[theme.breakpoints.down('xs')] : {
	  height: `calc(100% - 56px)`
	},
      },
      slider: {
	color:  theme.palette.primary.green,
      },
    }),
);


const CloseBtn = withRouter(({history}) => {
  const { t } = useTranslation();
  return (
    <Button color='secondary'
	    onClick={() => history.goBack()}>
      { t('back') }
    </Button>
  );
});


export default () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { authUser, updateAuthUser } = useContext(AuthContext);
  const [img, setImg] = useState(authUser.photoURL);
  const [name, setName] = useState(authUser.name);
  const [about, setAbout] = useState(authUser.about);
  const [blob, setBlob] = useState(null);
  const [scale, setScale] = useState(100);
  const [saving, setSaving] = useState(false);
  const [openEditor, setOpenEditor] = useState(false);
  const editorRef = useRef();
  const uploadBtnRef = useRef();

  const cropImage = async () => {
    if (editorRef.current) {
      const imgScaled = editorRef.current.getImageScaledToCanvas().toDataURL();

      const res = await fetch(imgScaled);
      const blob = await res.blob();
      setBlob(blob);
      setImg(URL.createObjectURL(blob));
      setOpenEditor(false);
    }
  };

  const updateProfile = async () => {
    setSaving(true);
    try {
      if (authUser.photoURL !== img) {
	const snap = await firebase.storage().ref().child('trainersAvatars/' + authUser.email).put(blob);
	const url = await snap.ref.getDownloadURL();
	const proms = [firebase.auth().currentUser.updateProfile({photoURL: url, displayName: name}),
		       firebase.firestore().collection('trainers').doc(authUser.email)
		       .collection('profile').doc('details').set({avatar: url, name, about}, {merge: true})];
	await Promise.all(proms);
	updateAuthUser({photoURL: img, name, about});
	setSaving(false);
	return;
      }

      await firebase.firestore().collection('trainers').doc(authUser.email)
	.collection('profile').doc('details').set({name, about}, {merge: true});
      updateAuthUser({name, about});
      setSaving(false);
    }
    catch (err) {
      setSaving(false);
      console.log('updating profile ', err);
    }
  };

  return (
    <div style={{height: '100%'}}>
      <HeaderBar LComp={<CloseBtn/>}
		 MComp={<Typography variant='h6' align='center'>{t('profile')}</Typography>}
		 RComp={<div></div>}/>
      <Grid container direction='column' justify='center' alignItems='center' className={classes.container}>
	<Grid item>
	  <input accept='image/*'
		 onChange={(e) => {
		   setOpenEditor(true);
		   setImg(URL.createObjectURL(uploadBtnRef.current.files[0]));
		 }}
		 style={{ display: 'none' }}
		 id='upload-input'
		 ref={uploadBtnRef}
		 type='file'/>
	  <label htmlFor='upload-input'>
	    <Avatar alt='avatar' src={img} className={classes.avatar} />
	  </label>
	</Grid>
	<Grid container direction='column' style={{padding: '1rem'}}>
	  <Typography gutterBottom variant='caption'>
	    { t('name') }
	  </Typography>
	  <Input
	    defaultValue={name}
	    onChange={(e) => {setName(e.target.value.trim());}}/>
	</Grid>
	<Grid item container direction='column' style={{padding: '1rem'}}>
	  <Typography gutterBottom variant='caption'>
	    { t('about-trainer') }
	  </Typography>
	  <Input
	    defaultValue={about}
	    multiline
	    rows={4}
	    onChange={(e) => {setAbout(e.target.value.trim());}}
	    inputProps={{'aria-label': t('about-trainer') }}/>
	</Grid>
	<Grid item container justify='center'>
	  {
	    saving ? (<CircularProgress color='secondary' />) :
	      <Button label='save'
		      variant='contained'
		      disabled={(name === authUser.name) && (about === authUser.about) && (authUser.photoURL === img)}
		      className={classes.saveBtn}
		      onClick={() => updateProfile()}>
		{ t('save') }
	      </Button>
	  }
	</Grid>
	<Dialog fullScreen
		open={openEditor}
		onClose={()=> {}}
		TransitionComponent={Transition}>
	  <AppBar className={classes.appBar}>
	    <Toolbar>
	      <Grid container>
		<Grid item xs={3}>
		  <Button color='secondary' onClick={()=>{}}>
		    { t('cancel') }
		  </Button>
		</Grid>
		<Grid item xs={6}>
		  <Typography edge='center' variant='h6' className={classes.title}>
		    {/* { replyDialogState.isAccepted ? t('pass-exam') : t('fail-exam')} */}
		  </Typography>
		</Grid>
		<Grid item xs={3} container justify='flex-end'>
		  <Button color='secondary' onClick={() => cropImage()}>
		    { t('choose') }
		  </Button>
		</Grid>
	      </Grid>
	    </Toolbar>
	  </AppBar>
	  <Grid container direction='column' justify='center' alignItems='center' className={classes.avatarEditorContainer}>
	    <Grid item>
	      <AvatarEditor
		ref={editorRef}
		image={img}
		borderRadius={200}
		className={classes.canvas}
		width={300}
		height={300}
		color={[255, 255, 255, 0.6]}
		scale={scale/100}
		rotate={0}/>
	    </Grid>
	    <Grid item container direction='column' style={{width: '200px'}}>
	      <Grid item>
		<Typography variant='subtitle2' className={classes.title}>
		  {t('zoom')}
		</Typography>
	      </Grid>
	      <Grid item >
		<Slider value={scale}
			min={100}
			max={600}
			className={classes.slider}
			onChange={(e, val) => {
			  setScale(val);
			}} />
	      </Grid>
	    </Grid>
	  </Grid>
	</Dialog>
      </Grid>
    </div>
  );
};
